import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { authoritiesAPI } from '../api/authorities';

const PREPAREDNESS_ITEMS = [
  {
    key: 'identification_required',
    label: 'Identification required',
    description: 'Must a voter bring identification to vote in person?',
  },
  {
    key: 'photo_id_required',
    label: 'Photo identification required',
    description: 'Must the identification contain a photo?',
  },
  {
    key: 'registration_card_required',
    label: 'Voter registration card required',
    description: 'Must the voter bring their voter registration card?',
  },
  {
    key: 'same_day_registration',
    label: 'Same-day registration',
    description: 'Is same-day voter registration available?',
  },
  {
    key: 'countywide_voting',
    label: 'Vote center / countywide voting',
    description: 'Can a voter use any Election Day vote center in the jurisdiction?',
  },
  {
    key: 'paper_notes_allowed',
    label: 'Sample ballot & notes allowed',
    description:
      'May the voter bring a paper sample ballot or written notes into the voting booth?',
  },
  {
    key: 'phone_camera_restricted',
    label: 'Phones & cameras restricted',
    description: 'Are phones or cameras restricted inside the polling place or voting booth?',
  },
  {
    key: 'curbside_voting_available',
    label: 'Curbside voting',
    description: 'Is curbside voting available for eligible voters?',
  },
  {
    key: 'voting_assistance_available',
    label: 'Voting assistance',
    description: 'Can an eligible voter receive assistance while voting?',
  },
];

const STATUS_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'conditional', label: 'Conditional' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'not_applicable', label: 'N/A' },
];

function PreparednessCard({ item, value, isPending, onSave, error }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? {});
  const [localError, setLocalError] = useState('');

  const handleSave = () => {
    if (!draft.status) {
      setLocalError('Status is required');
      return;
    }
    if (draft.sourceUrl) {
      try {
        new URL(draft.sourceUrl);
      } catch {
        setLocalError('Invalid URL (include https://)');
        return;
      }
    }
    onSave(draft);
  };

  const currentStatus = value?.status ?? 'unknown';
  const currentDetails = value?.details ?? '';
  const currentSourceUrl = value?.sourceUrl ?? '';

  if (editing) {
    return (
      <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-neutral-900 text-sm">{item.label}</h4>
        <p className="text-xs text-neutral-600">{item.description}</p>

        <div>
          <label className="text-xs font-medium text-neutral-700 block mb-2">Status</label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDraft({ ...draft, status: opt.value })}
                className={`px-3 py-1 text-xs rounded-lg font-medium transition ${
                  draft.status === opt.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-neutral-700 block mb-1">Details</label>
          <textarea
            value={draft.details ?? ''}
            onChange={(e) => setDraft({ ...draft, details: e.target.value || null })}
            placeholder="Short explanation of the rule..."
            className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows="2"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-neutral-700 block mb-1">
            Official source URL
          </label>
          <input
            type="url"
            value={draft.sourceUrl ?? ''}
            onChange={(e) => setDraft({ ...draft, sourceUrl: e.target.value || null })}
            placeholder="https://official.gov/..."
            className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {(localError || error) && <p className="text-xs text-red-500">{localError || error}</p>}

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => {
              setEditing(false);
              setLocalError('');
              setDraft(value ?? {});
            }}
            className="px-3 py-1 text-xs bg-neutral-100 text-neutral-600 rounded-lg hover:bg-neutral-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-neutral-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-neutral-900 text-sm">{item.label}</h4>
        <button
          onClick={() => {
            setDraft(value ?? {});
            setEditing(true);
          }}
          className="text-xs px-2 py-0.5 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-neutral-500 shrink-0"
        >
          Edit
        </button>
      </div>
      <p className="text-xs text-neutral-600 mb-3">{item.description}</p>

      {currentStatus && (
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              currentStatus === 'yes'
                ? 'bg-emerald-100 text-emerald-700'
                : currentStatus === 'no'
                  ? 'bg-red-100 text-red-700'
                  : currentStatus === 'conditional'
                    ? 'bg-amber-100 text-amber-700'
                    : currentStatus === 'unknown'
                      ? 'bg-neutral-100 text-neutral-600'
                      : 'bg-gray-100 text-gray-600'
            }`}
          >
            {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
          </span>
        </div>
      )}

      {currentDetails && <p className="text-xs text-neutral-700 mb-2">{currentDetails}</p>}

      {currentSourceUrl && (
        <a
          href={currentSourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary-600 hover:text-primary-700 block truncate"
        >
          {currentSourceUrl}
        </a>
      )}

      {!currentStatus && (
        <p className="text-xs text-neutral-400 italic">No information entered yet.</p>
      )}
    </div>
  );
}

export default function AdminVoterPreparedness({ authority, authorityId, qc }) {
  const mutation = useMutation({
    mutationFn: (data) =>
      authoritiesAPI.savePreparedness(
        authorityId,
        data.key,
        data.status,
        data.details,
        data.sourceUrl
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authority-detail', authorityId] });
    },
  });

  const preparedness = authority.votingPreparedness || {};

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600 mb-4">
        What does a voter need to know before going to vote?
      </p>
      <div className="grid grid-cols-1 gap-4">
        {PREPAREDNESS_ITEMS.map((item) => (
          <PreparednessCard
            key={item.key}
            item={item}
            value={preparedness[item.key]}
            isPending={mutation.isPending}
            onSave={(data) =>
              mutation.mutate({
                key: item.key,
                status: data.status,
                details: data.details,
                sourceUrl: data.sourceUrl,
              })
            }
            error={mutation.error?.response?.data?.message}
          />
        ))}
      </div>
    </div>
  );
}
